/**
 * Table — dense data table primitive.
 *
 * Composed pieces:
 *   <Table>
 *     <THead>
 *       <TR>
 *         <TH select />                   // checkbox column
 *         <TH sortable onSort={...}>Phòng</TH>
 *         <TH align="right">Số tiền</TH>
 *       </TR>
 *     </THead>
 *     <TBody>
 *       {rows.map(r => <Row key={r.id} selected={...} onClick={...}>...</Row>)}
 *     </TBody>
 *   </Table>
 *
 * Rules:
 *   - Default row height is 40px (md). Pass `density="compact"` for 36px.
 *   - Numeric columns: pass `align="right"` AND add `className="num"` to cells.
 *   - Sticky headers come for free via `<THead sticky>`.
 *   - Selected rows highlight with teal-50 + left band.
 *   - Use Tailwind for cell content — the Table primitives provide structure only.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp, ArrowDown, Sort as ArrowUpDown } from './icons';

const DensityCtx = React.createContext('md');

export function Table({ density = 'md', className = '', children }) {
  return (
    <DensityCtx.Provider value={density}>
      <div className={['w-full overflow-auto bg-white border border-stone-200 rounded-lg scrollbar-thin', className].join(' ')}>
        <table className="w-full border-collapse text-sm">
          {children}
        </table>
      </div>
    </DensityCtx.Provider>
  );
}

export function THead({ sticky = true, className = '', children }) {
  return (
    <thead className={[
      'bg-stone-50',
      sticky && 'sticky top-0 z-10',
      className,
    ].filter(Boolean).join(' ')}>
      {children}
    </thead>
  );
}

export function TBody({ className = '', children }) {
  return <tbody className={className}>{children}</tbody>;
}

/** Header cell. `sortable` shows the sort indicator and makes it clickable. */
export function TH({
  align = 'left',
  sortable,
  sortDir,             // 'asc' | 'desc' | null
  onSort,
  select,              // shows a checkbox header instead
  className = '',
  children,
  ...rest
}) {
  const alignCls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th
      onClick={sortable ? onSort : undefined}
      className={[
        'h-9 px-3 font-semibold text-2xs uppercase tracking-wider text-stone-500',
        'border-b border-stone-200',
        alignCls,
        sortable && 'cursor-pointer select-none hover:text-stone-700',
        className,
      ].filter(Boolean).join(' ')}
      {...rest}
    >
      {select ? (
        <input type="checkbox" className="accent-teal-600 w-3.5 h-3.5" {...rest} />
      ) : sortable ? (
        <span className="inline-flex items-center gap-1">
          {children}
          {sortDir === 'asc' ? <ArrowUp size={10} /> : sortDir === 'desc' ? <ArrowDown size={10} /> : <ArrowUpDown size={10} className="opacity-50" />}
        </span>
      ) : children}
    </th>
  );
}

/** Body row. `selected` highlights with primary tint + a left band. */
export function Row({
  selected,
  onClick,
  className = '',
  children,
  ...rest
}) {
  const density = React.useContext(DensityCtx);
  const h = density === 'compact' ? 'h-9' : 'h-10';
  return (
    <tr
      onClick={onClick}
      data-selected={selected || undefined}
      className={[
        h,
        'border-b border-stone-100 transition-colors relative',
        onClick && 'cursor-pointer hover:bg-stone-50',
        selected && '!bg-teal-50',
        'data-[selected=true]:relative',
        className,
      ].filter(Boolean).join(' ')}
      {...rest}
    >
      {selected && (
        // Decorative left band — appears via the first cell's :before
        <td className="!p-0 !w-[3px] !bg-teal-600 border-0" aria-hidden />
      )}
      {React.Children.map(children, (child, i) => {
        if (selected && i === 0 && child && child.type === Cell) {
          // Skip — first cell already has its slot, the band td handled it
        }
        return child;
      })}
    </tr>
  );
}

/** Body cell. Use `className="num"` for numeric / tabular content. */
export function Cell({
  align = 'left',
  className = '',
  children,
  ...rest
}) {
  const alignCls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <td className={['px-3 align-middle text-stone-700', alignCls, className].join(' ')} {...rest}>
      {children}
    </td>
  );
}

/** TR — Header row (use Row for body rows). */
export const TR = ({ children, ...rest }) => <tr {...rest}>{children}</tr>;

// ─── Pagination ─────────────────────────────────────────────────────────────
import { ChevronLeft, ChevronRight } from './icons';
import { Button } from './Button';

export function Pagination({ page, pageCount, onChange, total, pageSize, className = '' }) {
  const { t } = useTranslation('common');
  if (pageCount <= 1 && !total) return null;
  const from = (page - 1) * (pageSize || 1) + 1;
  const to = Math.min(page * (pageSize || 1), total || 0);

  // Build page list with ellipses
  const pages = buildPageList(page, pageCount);

  return (
    <div className={['flex items-center justify-between gap-3 px-1 py-2 text-xs', className].join(' ')}>
      <span className="text-stone-500">
        {total != null
          ? t('table.showing', { from, to, total })
          : t('table.page', { page, pageCount })}
      </span>
      <div className="flex items-center gap-1">
        <Button kind="ghost" size="sm" iconLeft={<ChevronLeft />} aria-label={t('table.prevPage')}
          disabled={page <= 1} onClick={() => onChange(page - 1)} />
        {pages.map((p, i) =>
          p === '…' ? (
            <span key={`e${i}`} className="px-1 text-stone-400">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p)}
              aria-current={p === page ? 'page' : undefined}
              className={[
                'ams-focus-ring num min-w-7 h-7 px-2 rounded text-xs font-medium',
                p === page ? 'bg-teal-600 text-white' : 'text-stone-700 hover:bg-stone-100',
              ].join(' ')}
            >{p}</button>
          )
        )}
        <Button kind="ghost" size="sm" iconLeft={<ChevronRight />} aria-label={t('table.nextPage')}
          disabled={page >= pageCount} onClick={() => onChange(page + 1)} />
      </div>
    </div>
  );
}

function buildPageList(page, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  if (page <= 4) return [1, 2, 3, 4, 5, '…', total];
  if (page >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
  return [1, '…', page - 1, page, page + 1, '…', total];
}
